import {httpCreateEvent, HttpCreateEventOptions} from '@/features/agenda/http/agenda.http'
import {EventSmall} from '@/features/agenda/types'
import {MUTATION_KEYS, QUERY_KEYS, queryClient} from '@/queryClient'
import {FormErrorResponse} from '@/types'
import {useMutation, UseMutationOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'

export const useStoreEvent = (
    options: Omit<
        UseMutationOptions<EventSmall[], AxiosError<FormErrorResponse>, HttpCreateEventOptions['payload']>,
        'mutationFn'
    >
) => {
    return useMutation({
        ...options,
        mutationKey: [MUTATION_KEYS.CREATE_EVENT],
        mutationFn: payload => httpCreateEvent({payload}),
        onSuccess: (data, variables, context) => {
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ACCESSES]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECTS]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PLANNED_ACCESSES]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT_ACCESS_COUNTERS]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.AGENDA_EVENTS_COUNT]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.AGENDA_AVAILABILITIES]})
            // Invalidate the whole AGENDA_EVENTS tree so EVERY consumer refetches with the new event:
            // the calendar list ([AGENDA_EVENTS, {therapists, patients, startDate, endDate, ...}]) and the
            // per-therapist lists, the open event detail ([AGENDA_EVENTS, 'extended', id]) and the project
            // warning banner ([AGENDA_EVENTS, 'project-warning-banner']). The previous 'extended'-only
            // invalidation matched the detail key but never the calendar list keys (their second element is
            // a filters object, not the 'extended' string), so a freshly created event did not show up in
            // the currently displayed agenda. The socket events:created handler still patches the cache
            // optimistically; this invalidation is the deterministic fallback when it does not fire.
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.AGENDA_EVENTS]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.TREATMENT_REQUESTS_GROUPS]})

            if (variables.type !== 'generic_event') {
                for (const patient of variables.eventPatients) {
                    void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT, patient.projectId]})
                }
            }

            options?.onSuccess?.(data, variables, context)
        }
    })
}
